import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PageBanner from '../components/PageBanner';
import CategoryProductList from '../components/CategoryProductList';
import Seo, { breadcrumbSchema, SITE_URL } from '../components/Seo';
import { fetchAllProducts, fetchCategories } from '../api/catalog';
import { mapApiProductToWaveItem } from '../utils/catalogDisplay';
import { PAGE_BANNER_IMAGES } from '../data/homeData';
import '../Home.css';
import '../PageBanner.css';
import '../ProductDetail.css';

function CategoryProductsPage() {
  const { categorySlug } = useParams();
  const [category, setCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [categorySlug]);

  useEffect(() => {
    let cancelled = false;

    async function loadCategory() {
      setLoading(true);
      setError('');

      try {
        const [categories, productRows] = await Promise.all([
          fetchCategories(),
          fetchAllProducts({ category_slug: categorySlug }),
        ]);

        if (cancelled) return;

        const match = categories.find((row) => row.slug === categorySlug);
        if (!match) {
          setError('Category not found.');
          return;
        }

        setCategory(match);
        setProducts(productRows.map(mapApiProductToWaveItem));
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Unable to load category products.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadCategory();

    return () => {
      cancelled = true;
    };
  }, [categorySlug]);

  const categoryName = category?.name || 'Indian grocery category';
  const description = category?.description
    ? `${category.description} Wholesale supply from India for UK importers, distributors and retailers.`
    : `Browse export-ready ${categoryName.toLowerCase()} for UK wholesale import from India.`;
  const canonicalPath = `/products/category/${categorySlug}`;
  const categorySchema = category
    ? [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: `${category.name} for UK wholesale import`,
        url: `${SITE_URL}${canonicalPath}`,
        description,
        inLanguage: 'en-GB',
        isPartOf: { '@id': `${SITE_URL}/#website` },
      },
      breadcrumbSchema([
        { name: 'Home', path: '/' },
        { name: 'Products', path: '/products' },
        { name: category.name, path: canonicalPath },
      ]),
    ]
    : null;

  return (
    <div className="page">
      <Seo
        title={`${categoryName} for UK Wholesale Import`}
        description={description}
        path={canonicalPath}
        noindex={Boolean(error)}
        schema={categorySchema}
      />
      <Header />
      <main className="main products-page-main">
        <PageBanner
          image={PAGE_BANNER_IMAGES.category}
          eyebrow={category ? category.name : 'Category'}
          title={category?.name || 'Loading category…'}
          lead={
            category?.description ||
            (category ? `Browse our full ${category.name} export range for UK wholesale.` : '')
          }
          breadcrumbs={[
            { label: 'Products', to: '/products' },
            { label: category?.name || 'Category' },
          ]}
        />

        <section className="pd-category-body">
          <div className="pd-shell">
            {loading && <p className="product-status">Loading category…</p>}

            {!loading && error && (
              <div className="pd-not-found">
                <h2>Category not found</h2>
                <p>{error}</p>
                <Link to="/products" className="pd-back-link">
                  <ArrowLeft size={16} />
                  Back to all categories
                </Link>
              </div>
            )}
          </div>
        </section>

        {!loading && !error && products.length > 0 && (
          <CategoryProductList
            products={products}
            categoryName={category?.name || ''}
            sectionTitle={`All ${category?.name || ''} products`}
          />
        )}
      </main>
      <Footer />
    </div>
  );
}

export default CategoryProductsPage;
